// Module 16(b) §7 — the DTO→domain layer between `ExportJobApi` and `dataExportStore`.
//
// The jobs mapper is lifted 1:1 from `DataExportTable.vue:261-277`, where twelve flat fields were
// derived from `common_fields`/`partitions` inside a component's `getData()`. Only the legacy
// DEFECTS are corrected (standing rule: port the design, never the bugs):
//   • a job with no `partitions` produced `undefined` for `partition_name`/`partition_array`
//     (`:274-275`) — now `''` / `[]`, so the list never renders "undefined".
//   • a job whose `email` is absent produced `[undefined]` for `email_array` (`:271-273`) — the
//     nullish entry is dropped instead, so a recipient count is never off by one.
// Neither changes a populated row's output.
import { exportJobApi, type ExportJobApi } from '../api/ExportJobApi'
import type {
  ExportBreakdown,
  ExportColumnSelection,
  ExportJob,
  ExportJobCreateAck,
  ExportJobDto,
  ExportJobPayload,
  ExportTemplate,
  ExportTemplateColumnDto,
  ExportTemplateDto,
} from '../models/exportJob.types'

function text(value: unknown): string {
  return typeof value === 'string' ? value : typeof value === 'number' ? String(value) : ''
}

/**
 * `email` arrives as a STRING or an ARRAY depending on how the job was created
 * (`DataExportTable.vue:268-273`), so both derived fields branch on `Array.isArray` exactly as
 * legacy did: `email` is the joined display string, `email_array` the addressable list.
 */
function readEmails(email: string | string[] | undefined): { joined: string; list: string[] } {
  const list = (Array.isArray(email) ? email : [email])
    .map((entry) => text(entry).trim())
    .filter((entry) => entry.length > 0)
  return { joined: list.join(', '), list }
}

/** The `DataExportTable.vue:261-277` flattening, verbatim in shape and field order. */
export function toExportJob(dto: ExportJobDto): ExportJob {
  const common = dto.common_fields
  const partitions = dto.partitions ?? []
  const emails = readEmails(common?.notification_config?.email)
  const accountNames = partitions.map((partition) => text(partition.account_name))
  const platforms = partitions.map((partition) => text(partition.platform)).filter(Boolean)

  return {
    ...dto,
    id: common?.id,
    end_date: common?.date_range_config?.end_date,
    start_date: common?.date_range_config?.start_date,
    concatenate: common?.destination_options?.concatenate,
    export_now: common?.destination_options?.export_now,
    email: emails.joined,
    email_array: emails.list,
    partition_name: accountNames.join(', '),
    partition_array: accountNames,
    sources: [...new Set(platforms)],
  }
}

function toColumn(dto: ExportTemplateColumnDto, numeric: boolean): ExportColumnSelection {
  const identifier = text(dto.identifier)
  return {
    ...dto,
    identifier,
    // Legacy aliased `identifier`→`field` and `name`→`header` so the shared column widgets could
    // consume a template column unchanged (`DataExportCreate.vue:440-446`).
    field: identifier,
    header: text(dto.name),
    numeric,
  }
}

function toBreakdown(dto: ExportTemplateColumnDto): ExportBreakdown {
  return { identifier: text(dto.identifier), name: text(dto.name) }
}

/** `DataExportCreate.vue:436-449` — metrics are flagged `numeric`, dimensions are not. */
export function toExportTemplate(dto: ExportTemplateDto): ExportTemplate {
  const description = text(dto.description)
  return {
    identifier: text(dto.identifier),
    name: text(dto.name),
    description,
    visibleHeader: description.replace('Custom ', ''),
    dimensions: (dto.dimensions ?? []).map((column) => toColumn(column, false)),
    metrics: (dto.metrics ?? []).map((column) => toColumn(column, true)),
    breakdowns: (dto.breakdowns ?? []).map(toBreakdown),
  }
}

export interface ExportRemainingCounts {
  oneTime: number
  scheduled: number
}

export interface ExportTemplateCatalog {
  templates: ExportTemplate[]
  breakdownCombinations: string[][]
}

/**
 * The feature's read/write funnel. Errors are NOT swallowed here — legacy's three `catch` blocks
 * (`DataExportCreate.vue:425/457`, `DataExportTable.vue:248/286/308`) each console-logged and left
 * the surface silently empty, which is exactly the "silent empty table" contract §14 forbids. The
 * `ApiError` reaches the store, and the store's caller owns the error state.
 */
export class ExportJobService {
  constructor(private readonly api: ExportJobApi = exportJobApi) {}

  async loadRemainingCounts(accountId: number | string): Promise<ExportRemainingCounts> {
    const dto = await this.api.fetchRemainingCounts(accountId)
    return {
      oneTime: dto.remaining_one_time_export_limit ?? 0,
      scheduled: dto.remaining_scheduled_export_limit ?? 0,
    }
  }

  async loadTemplates(platforms: readonly string[]): Promise<ExportTemplateCatalog> {
    const dto = await this.api.fetchTemplates(platforms)
    return {
      templates: (dto.message ?? []).map(toExportTemplate),
      breakdownCombinations: dto.breakdown_combinations ?? [],
    }
  }

  async loadJobs(
    accountUserId: number | string,
    workspaceId: number | string,
  ): Promise<ExportJob[]> {
    const rows = await this.api.fetchJobs(accountUserId, workspaceId)
    return rows.map(toExportJob)
  }

  async create(
    accountUserId: number | string,
    workspaceId: number | string,
    payload: ExportJobPayload,
  ): Promise<ExportJobCreateAck> {
    return this.api.create(accountUserId, workspaceId, payload)
  }

  async remove(
    accountUserId: number | string,
    workspaceId: number | string,
    groupId: string,
  ): Promise<void> {
    await this.api.remove(accountUserId, workspaceId, groupId)
  }
}

export const exportJobService = new ExportJobService()
